
import React from "react";
import { Button } from "@/components/ui/button";

interface FaithSelectorProps {
  selectedFaith: string;
  onFaithChange: (faith: string) => void;
  onSampleQuestionSelect?: (question: string) => void;
}

// Sample questions by faith group
const sampleQuestions: Record<string, string[]> = {
  islam: [
    "How does the Christian view of Jesus differ from the Islamic view?",
    "What does Christianity say about salvation compared to Islam?",
    "How do Christians respond to the claim that the Bible has been corrupted?",
  ],
  judaism: [
    "How do Christians interpret messianic prophecies differently than Jews?",
    "Why do Christians believe Jesus fulfilled the Law?",
    "How do Christians respond to Jewish objections about the Trinity?",
  ],
  hinduism: [
    "How does the Christian concept of God differ from Hinduism's many deities?",
    "What does Christianity teach about karma and reincarnation?",
    "How do Christians respond to the Hindu belief in multiple paths to salvation?",
  ],
  mormonism: [
    "What are the key differences between the Mormon Jesus and the Biblical Jesus?",
    "How do Christians respond to the claim of additional scripture like the Book of Mormon?",
    "What does the Bible say about modern prophets and continuing revelation?",
  ],
  jehovahs_witnesses: [
    "Why do Christians believe Jesus is God when Jehovah's Witnesses don't?",
    "How do Christians defend the doctrine of the Trinity?",
    "What does the Bible actually teach about the Holy Spirit's personhood?",
  ],
  black_hebrew_israelites: [
    "What does the Bible say about salvation being based on ethnicity?",
    "How do Christians understand the relationship between Israel and the Church?",
    "What does Paul mean when he says 'there is neither Jew nor Greek' in Christ?",
  ],
  religious_pluralism: [
    "Why do Christians believe Jesus is the only way to God?",
    "How do Christians respond to the claim that all religions lead to the same place?",
    "What makes Christianity's truth claims different from other religions?",
  ],
  universalism: [
    "What does the Bible teach about heaven, hell, and who will be saved?",
    "How do Christians respond to universalist interpretations of scripture?",
    "If God is loving, why wouldn't everyone be saved in the end?",
  ],
  new_age: [
    "How does the Christian understanding of God differ from pantheism?",
    "What does Christianity teach about finding truth within yourself?",
    "How do Christians respond to the belief that we create our own reality?",
  ],
};

const FaithSelector: React.FC<FaithSelectorProps> = ({
  selectedFaith,
  onFaithChange,
  onSampleQuestionSelect
}) => {
  const setQuestion = (sampleQ: string) => {
    if (onSampleQuestionSelect) {
      onSampleQuestionSelect(sampleQ);
    } else {
      // Fallback to console log if no handler provided
      console.log("Sample question selected:", sampleQ);
    }
  };

  return (
    <div className="mb-8">
      <label htmlFor="faith" className="block text-sm font-medium text-gray-700 mb-2">
        Select a Faith or Worldview:
      </label>
      <select
        id="faith"
        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
        value={selectedFaith}
        onChange={(e) => onFaithChange(e.target.value)}
      >
        <option value="">Choose a faith...</option>
        <option value="islam">Islam</option>
        <option value="judaism">Judaism</option>
        <option value="hinduism">Hinduism</option>
        <option value="mormonism">Mormonism</option>
        <option value="jehovahs_witnesses">Jehovah's Witnesses</option>
        <option value="black_hebrew_israelites">Black Hebrew Israelites</option>
        <option value="religious_pluralism">Religious Pluralism</option>
        <option value="universalism">Universalism</option>
        <option value="new_age">New Age</option>
      </select>

      {selectedFaith && sampleQuestions[selectedFaith] && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Questions:</h4>
          <div className="flex flex-col space-y-2">
            {sampleQuestions[selectedFaith].map((sampleQ, index) => (
              <Button 
                key={index} 
                variant="outline"
                className="justify-start text-left h-auto py-2 px-3 text-[13px] sm:text-sm sm:py-3 sm:px-4 font-normal whitespace-normal break-words"
                onClick={() => setQuestion(sampleQ)}
              >
                {sampleQ}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FaithSelector;
